import { Injectable, signal } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import {
    createTransportOrderFromLegEndpoint,
    createTransportOrderFromLegsEndpoint,
    createTransportOrderFromLotEndpoint,
    pickupPlanningTransportOrdersEndpoint,
    pickupPlanningTransportOrdersWorkflowEndpoint,
    transportOrderResourceEndpoint
} from '../configuration/consts/endpoints';
import { RequestService, SnackbarService, StorageService } from '@nagel-services';
import { environment } from 'apps/nagel-cal-disposition/environment/environment';
import { ERROR_SNACKBAR, INFO_SNACKBAR, PaginatorParams, SUCCESS_SNACKBAR } from '@nagel-types';
import { getTimezoneAdjustedDate, protectSubscription } from '@nagel-utils';
import {
    CreateTransportOrderFromLegsResponse,
    CreateTransportOrderRequest,
    CreateTransportOrderResponse,
    DraggableCardConfig,
    LotCardConfig,
    PickupPlanningGroupedTransportOrderResponse,
    PickupPlanningSingleTransportOrderResponse,
    TransportOrderDeleteResponse,
    TransportOrderGetRequestProps,
    TransportOrdersGetPayloadProps
} from '../../models/planningPageTypes';
import { PICKUP_PLANNING_TO_FILTERS_TYPES_TO_STRATEGY_MAP } from '../configuration/consts/filterStrategiesConsts';
import { FilterFieldMapperService } from './filter-field-mapper.service';
import { BatchLegResultService } from './batch-leg-result.service';
import { ConflictExceptionResponse, ConflictErrorType } from '../../models/assignmentsTypes';
import { WARNING_SNACKBAR } from 'libs/nagel-types/src/lib/snackbarTypes';
import { RESPONSE_CODES } from '@nagel-consts';

const shouldEmitSubscriptionOnce = true;

@Injectable({
    providedIn: 'root'
})
export class CrudPickupPlanningTransportOrdersService {
    refreshTransportOrdersSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
    refreshTransportOrdersObservable$ = this.refreshTransportOrdersSubject.asObservable();

    isLoadingSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    isLoadingObservable$ = this.isLoadingSubject.asObservable();

    isCreatingTransportOrder = signal<boolean>(false);

    private cancelRequestSubject$: Subject<boolean> = new Subject<boolean>();

    constructor(
        private requestService: RequestService,
        private snackbarService: SnackbarService,
        private filterFieldMapperService: FilterFieldMapperService,
        private batchLegResultService: BatchLegResultService,
        private localStorageService: StorageService
    ) { }

    setIsLoading = (isLoading: boolean) => {
        this.isLoadingSubject.next(isLoading);
    };

    //Delete
    deleteTransportOrder = (transportOrderId: number, transportOrderNumber: number) => {
        const url = `${environment.apiUrl}${transportOrderResourceEndpoint}/${transportOrderId}`;

        const deleteObservable = this.requestService.deleteRequest<undefined, TransportOrderDeleteResponse>(url, undefined, undefined, {}, true);

        protectSubscription(
            deleteObservable,
            (response) => this.onDeleteDataSuccessfulRetrieval(response, transportOrderNumber),
            (error) => this.onDeleteDataRetrievalError(error as ConflictExceptionResponse),
            shouldEmitSubscriptionOnce
        );
    };

    onDeleteDataSuccessfulRetrieval = (response: TransportOrderDeleteResponse, transportOrderNumber: number) => {
        const message = $localize`Transport order ${transportOrderNumber} deleted successfully`;
        this.setIsLoading(false);
        this.refreshTransportOrdersSubject.next(response.transportOrderId);
        this.snackbarService.pushSnackbar({ message, action: $localize`Close`, type: SUCCESS_SNACKBAR });
    };

    onDeleteDataRetrievalError = (error: ConflictExceptionResponse) => {
        this.setIsLoading(false);

        if (error.status === RESPONSE_CODES.CONFLICT) {
            const conflictErrors = error.error?.conflictErrors || [];
            const currentConflictError = conflictErrors[0] as ConflictErrorType;

            this.snackbarService.pushSnackbar({
                message: $localize`Transport Order with ID ${currentConflictError.TransportOrderId} deleted. Some local data could not be cleaned up.
                        Our team has been notified. Log ID: ${currentConflictError.IncidentId}.
                        This Log ID will no longer be accessible after dismissing this message`,
                action: 'OK',
                type: ERROR_SNACKBAR,
                persistent: true
            });
        } else {
            this.snackbarService.pushSnackbar({ message: error.message || '', action: $localize`Close`, type: ERROR_SNACKBAR });
        }

        console.error(error);
    };

    //Get
    private getTransportOrders = (payloadProps: TransportOrdersGetPayloadProps): Observable<PickupPlanningGroupedTransportOrderResponse> => {
        const url = `${environment.apiUrl}${pickupPlanningTransportOrdersEndpoint}`;
        const payload = this.buildGetPayload(payloadProps);

        return this.requestService.postRetrieveRequest<PaginatorParams, PickupPlanningGroupedTransportOrderResponse>(url, payload);
    };

    handleGetTransportOrdersRequest = (shouldOverrideExecutingRequest: boolean = false) => (getRequestProps: TransportOrderGetRequestProps) => {
        const {
            startDateValue,
            endDateValue,
            currentPageIndex,
            pageSize,
            softFiltersValues,
            responseSubject
        } = getRequestProps;

        if (startDateValue && endDateValue) {
            const payloadProps = {
                softFiltersValues,
                startDateValue: getTimezoneAdjustedDate(startDateValue),
                endDateValue: getTimezoneAdjustedDate(endDateValue),
                currentPageIndex,
                pageSize
            };

            const requestObservable = this.getTransportOrders(payloadProps);

            if (this.isLoadingSubject.value === false || shouldOverrideExecutingRequest) {
                this.setIsLoading(true);
                if (shouldOverrideExecutingRequest) {
                    this.cancelRequestSubject$.next(true);
                }

                protectSubscription<PickupPlanningGroupedTransportOrderResponse>(
                    requestObservable,
                    (data) => this.onDataSuccessfulRetrieval(data, responseSubject),
                    this.onDataRetrievalError,
                    shouldEmitSubscriptionOnce,
                    this.cancelRequestSubject$
                );
            }
        }
    };

    onDataSuccessfulRetrieval = (
        response: PickupPlanningGroupedTransportOrderResponse,
        responseSubject: BehaviorSubject<PickupPlanningGroupedTransportOrderResponse>
    ) => {
        responseSubject.next(response);
        this.setIsLoading(false);
    };

    onDataRetrievalError = (error: unknown) => {
        this.setIsLoading(false);
        console.error(error);
    };

    buildGetPayload = (props: TransportOrdersGetPayloadProps) => {
        const { startDateValue, endDateValue, softFiltersValues, pageSize, currentPageIndex } = props;

        return {
            groupsOffsetInfo: {
                offset: currentPageIndex * pageSize,
                limit: pageSize
            },
            dateFrom: startDateValue,
            dateTo: endDateValue,
            pinnedTransportOrderIds: this.localStorageService.getItem('transportOrderPinnedIds')?.split(', ').map(x => Number(x)),
            pinnedVehicleIds: this.localStorageService.getItem('vehicleGroupKeys')?.split(', '),
            filterCriteriaInsideGroups: this.filterFieldMapperService.generateFilterCriteria(
                softFiltersValues,
                PICKUP_PLANNING_TO_FILTERS_TYPES_TO_STRATEGY_MAP
            )
        };
    };

    //Get by id
    getSingleTransportOrder = (transportOrderId: number): Observable<PickupPlanningSingleTransportOrderResponse> => {
        const url = `${environment.apiUrl}${pickupPlanningTransportOrdersWorkflowEndpoint}/${transportOrderId}`;

        return this.requestService.getRequest<unknown, PickupPlanningSingleTransportOrderResponse>(url);
    };

    handleGetSingleTransportOrdersRequest = (
        transportOrderId: number,
        setUpdatedSingleTransportOrder: (transportOrder: PickupPlanningSingleTransportOrderResponse) => void,
        stopLoading: () => void
    ) => {
        const requestObservable = this.getSingleTransportOrder(transportOrderId);

        protectSubscription<PickupPlanningSingleTransportOrderResponse>(
            requestObservable,
            (data) => this.onGetSingleDataSuccessfulRetrieval(data, setUpdatedSingleTransportOrder, stopLoading),
            (error) => this.onGetSingleDataErrorRetrieval(error as Error, stopLoading),
            shouldEmitSubscriptionOnce
        );
    };

    onGetSingleDataSuccessfulRetrieval(
        response: PickupPlanningSingleTransportOrderResponse,
        setUpdatedSingleTransportOrder: (transportOrder: PickupPlanningSingleTransportOrderResponse) => void,
        stopLoading: () => void
    ) {
        setUpdatedSingleTransportOrder(response);
        stopLoading();
    }

    onGetSingleDataErrorRetrieval(error: Error, stopLoading: () => void) {
        stopLoading();
        console.error(error);
    }

    //Create
    isLotType(draggedCard: LotCardConfig): boolean {
        return !!draggedCard.shipments;
    }

    getCreateTransportOrderRequest(draggedCard: LotCardConfig, performanceDate: Date): Observable<CreateTransportOrderResponse> {
        const idType = this.isLotType(draggedCard) ? 'lotId' : 'legId';
        const url = idType === 'lotId'
            ? `${environment.apiUrl}${createTransportOrderFromLotEndpoint}`
            : `${environment.apiUrl}${createTransportOrderFromLegEndpoint}`;

        const requestPayload = {
            [idType]: draggedCard.id,
            performanceDate: getTimezoneAdjustedDate(performanceDate)
        };

        return this.requestService.postRequest<CreateTransportOrderRequest, CreateTransportOrderResponse>(url, requestPayload, undefined, undefined, true);
    }

    getCreateTransportOrderFromLegsRequest(draggedLegs: DraggableCardConfig[], performanceDate: Date): Observable<CreateTransportOrderFromLegsResponse> {
        const url = `${environment.apiUrl}${createTransportOrderFromLegsEndpoint}`;

        const requestPayload = {
            legIds: draggedLegs.map((leg: DraggableCardConfig) => leg.id),
            performanceDate: getTimezoneAdjustedDate(performanceDate)
        };

        return this.requestService.postRequest<CreateTransportOrderRequest, CreateTransportOrderFromLegsResponse>(url, requestPayload, undefined, undefined, true);
    }

    onCreateDataSuccessfulRetrieval = (onSuccess: () => void, response: CreateTransportOrderResponse) => {
        const successfulMessage = $localize`Transportorder ${response.transportOrderNumber} has been successfully created`;

        this.isCreatingTransportOrder.set(false);
        this.snackbarService.dismissSnackbar();

        this.snackbarService.pushSnackbar({
            message: successfulMessage,
            action: 'OK',
            type: SUCCESS_SNACKBAR
        });

        onSuccess && onSuccess();
    };

    onCreateDataErrorRetrieval(error: Error, draggedCard: LotCardConfig) {
        const isLot = this.isLotType(draggedCard);
        this.isCreatingTransportOrder.set(false);
        this.snackbarService.dismissSnackbar();

        isLot
            ? this.handleCreateTransportOrderFromLotError(error as ConflictExceptionResponse)
            : this.handleCreateTransportOrderFromLegError(error as ConflictExceptionResponse);
    }

    handleCreateTransportOrderFromLegError = (error: ConflictExceptionResponse) => {
        if (error.status === RESPONSE_CODES.CONFLICT) {
            const conflictError = error.error?.conflictErrors[0];

            if (conflictError?.IsSuccessfullyResolved) {
                this.snackbarService.pushSnackbar({
                    message: $localize`The creation of a transport order from leg affected an out-of-sync item.
                        The issue has been resolved automatically. Please refresh the page.`,
                    action: 'OK',
                    type: WARNING_SNACKBAR,
                    persistent: true
                });
            } else {
                this.snackbarService.pushSnackbar({
                    message: $localize`Conflict detected, which could not be resolved automatically.
                        Some local data could not be cleaned up. Our team has been notified. Log ID: ${conflictError?.IncidentId}.
                        This Log ID will no longer be accessible after dismissing this message.`,
                    action: 'OK',
                    type: ERROR_SNACKBAR,
                    persistent: true
                });
            }
        } else {
            this.snackbarService.pushSnackbar({
                message: error.message || $localize`Error creating transport order from leg.`,
                action: 'OK',
                type: ERROR_SNACKBAR
            });
        }
    };

    private showIndividualLegAssignErrorSnackbar(conflicts: ConflictErrorType[]): void {
        conflicts.forEach(conflict => {
            if (conflict.IsSuccessfullyResolved) {
                this.snackbarService.pushSnackbar({
                    message: $localize`The creation of a transport order from a lot affected an out-of-sync item - leg with ID ${conflict.AffectedEntityId}.
                        The issue has been resolved automatically. Please refresh the page.`,
                    action: 'OK',
                    type: WARNING_SNACKBAR,
                    persistent: true
                });
            } else {
                this.snackbarService.pushSnackbar({
                    message: $localize`Conflict detected on leg ${conflict.AffectedEntityId}.
                        Some local data could not be cleaned up. Our team has been notified. Log ID: ${conflict.IncidentId}.
                        This Log ID will no longer be accessible after dismissing this message.`,
                    action: 'OK',
                    type: ERROR_SNACKBAR,
                    persistent: true
                });
            }
        });
    }

    handleCreateTransportOrderFromLotError(error: ConflictExceptionResponse, onError?: () => void) {
        if (error.status === RESPONSE_CODES.CONFLICT) {
            const {error: internalError} = error;
            this.showIndividualLegAssignErrorSnackbar(internalError?.conflictErrors || []);
        } else {
            this.snackbarService.pushSnackbar({
                message: error.message || $localize`Error creating transport order from lot.`,
                action: 'OK',
                type: ERROR_SNACKBAR
            });
        }

        onError && onError();
    }

    createTransportOrder(
        draggedCard: LotCardConfig,
        onSuccess: () => void,
        performanceDate: Date
    ) {
        this.isCreatingTransportOrder.set(true);

        const creatingTOMessage = $localize`A new transportorder is being created.`;

        this.snackbarService.pushSnackbar({
            message: creatingTOMessage,
            action: 'OK',
            type: INFO_SNACKBAR
        });

        const createObservable$ = this.getCreateTransportOrderRequest(draggedCard, performanceDate);

        protectSubscription(
            createObservable$,
            (response) => this.onCreateDataSuccessfulRetrieval(onSuccess, response),
            (error) => this.onCreateDataErrorRetrieval(error as Error, draggedCard),
            shouldEmitSubscriptionOnce
        );
    }

    createTransportOrderFromLegs(
        draggedLegs: DraggableCardConfig[],
        onSuccess: () => void,
        performanceDate: Date
    ) {
        this.isCreatingTransportOrder.set(true);

        const creatingTOMessage = $localize`A new transportorder is being created.`;

        this.snackbarService.pushSnackbar({
            message: creatingTOMessage,
            action: 'OK',
            type: INFO_SNACKBAR
        });

        const createObservable$ = this.getCreateTransportOrderFromLegsRequest(draggedLegs, performanceDate);

        protectSubscription(
            createObservable$,
            (response) => this.onCreateFromLegsSuccessfulRetrieval(onSuccess, response),
            (error) => this.onCreateFromLegsErrorRetrieval(error as Error),
            shouldEmitSubscriptionOnce
        );
    }

    onCreateFromLegsSuccessfulRetrieval = (onSuccess: () => void, response: CreateTransportOrderFromLegsResponse) => {
        const { transportOrderNumber, legsResponse } = response;

        this.isCreatingTransportOrder.set(false);
        this.snackbarService.dismissSnackbar();

        this.batchLegResultService.handleBatchResult(
            this.batchLegResultService.toBatchLegResult(legsResponse),
            {
                success: $localize`Transportorder ${transportOrderNumber} has been successfully created`,
                failure: transportOrderNumber
                    ? $localize`Transportorder ${transportOrderNumber} has been created, but none of the legs could be added to it.`
                    : $localize`Transportorder could not be created because none of the legs could be added.`,
                partial: (succeededCount: number, totalCount: number) =>
                    $localize`Transportorder ${transportOrderNumber} has been created with ${succeededCount} of ${totalCount} legs.`
            },
            onSuccess,
            {
                resolved: (legId: string) =>
                    $localize`The creation of a transport order from legs affected an out-of-sync item - leg with ID ${legId}.
                        The issue has been resolved automatically. Please refresh the page.`,
                failed: (legId: string, reason: string) =>
                    $localize`Leg with ID ${legId} could not be added to Transportorder ${transportOrderNumber}. ${reason}`
            }
        );
    };

    onCreateFromLegsErrorRetrieval(error: Error) {
        this.isCreatingTransportOrder.set(false);
        this.snackbarService.dismissSnackbar();

        this.handleCreateTransportOrderFromLegError(error as ConflictExceptionResponse);
    }
}